import type { GmbAuditReport } from '@/types/gmb';
import {
  buildDiagnosisDimensions,
  resolveMaxGapDimension,
} from '@/pages/gmb-audit/utils/diagnosisDimensions';
import { formatMetricDisplayLabel } from '@/pages/gmb-audit/utils/reportDisplay';

// V1 Conversion-1｜健診報告 → 諮詢頁 的最小必要顯示 context。
// 全部取自 Backend 既有結果（target / scoreResult / priorities），
// 不重算 score / grade / priority、不重新查 Google / Peer / Snapshot / History。
export interface ConsultReportContext {
  /** 商家顯示名稱 */
  displayName: string;
  /** 健康度分數；scoreMode = INSUFFICIENT 時為 undefined */
  score?: number;
  /** Backend scoreMode（FULL / PARTIAL / INSUFFICIENT / CLOSED） */
  scoreMode: string;
  /** 是否為資料不足（不得顯示 0 / 100） */
  scoreInsufficient: boolean;
  /** 健康度等級 */
  healthGrade?: string;
  /** 健康度等級中文 */
  healthGradeLabel?: string;
  /** 最大缺口構面名稱 */
  largestGapLabel?: string;
  /** Top Priority 第一項顯示名稱 */
  priorityLabel?: string;
}

const STORAGE_KEY = 'gmb_consult_context_v1';

const GRADE_FALLBACK_LABEL: Record<string, string> = {
  A: '優良',
  B: '良好',
  C: '尚可',
  D: '待加強',
  E: '優先改善',
};

/** 由一份已完成報告組出諮詢頁要顯示的 context（只讀既有結果）。 */
export function buildConsultContext(report: GmbAuditReport): ConsultReportContext {
  const { scoreResult, target } = report;
  const dimensions = buildDiagnosisDimensions(scoreResult.metrics);
  const maxGap = resolveMaxGapDimension(dimensions);
  const topPriority = (scoreResult.priorities || [])[0];
  const scoreInsufficient =
    scoreResult.scoreMode !== 'CLOSED' &&
    (scoreResult.scoreMode === 'INSUFFICIENT' || typeof scoreResult.score !== 'number');

  return {
    displayName: target?.displayName || '',
    score: typeof scoreResult.score === 'number' ? scoreResult.score : undefined,
    scoreMode: scoreResult.scoreMode,
    scoreInsufficient,
    healthGrade: scoreResult.healthGrade,
    healthGradeLabel:
      scoreResult.healthLabel ||
      (scoreResult.healthGrade ? GRADE_FALLBACK_LABEL[scoreResult.healthGrade] : undefined),
    largestGapLabel: maxGap ? maxGap.title : undefined,
    priorityLabel: topPriority
      ? formatMetricDisplayLabel(topPriority.metricKey, topPriority.title)
      : undefined,
  };
}

/** 只保存最少必要欄位（refresh fallback）；絕不保存 token / 完整 report / peer 資料。 */
export function saveConsultContext(context: ConsultReportContext): void {
  try {
    const minimal = {
      displayName: context.displayName,
      score: context.score,
      scoreMode: context.scoreMode,
      scoreInsufficient: context.scoreInsufficient,
      healthGrade: context.healthGrade,
      healthGradeLabel: context.healthGradeLabel,
      largestGapLabel: context.largestGapLabel,
      priorityLabel: context.priorityLabel,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(minimal));
  } catch {
    // sessionStorage 不可用時不影響頁面使用。
  }
}

/** 讀回 context（僅 refresh fallback 用）。 */
export function readConsultContext(): ConsultReportContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsultReportContext;
    if (!parsed || typeof parsed !== 'object' || !('displayName' in parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}